import { Icon, Text } from '@pairy/pds-react';
import cn from 'classnames';
import { useEffect, useRef } from 'react';

import { EmbedType, EmbedItemType } from '../../PdfViewer/index.types';
import { V2DocumentType, V2PageType } from '../index.types';

import * as Styled from './index.styles';

interface Props {
  document: V2DocumentType;
  currentPage: number;
  goToPage: (page: number) => void;
  blindPages: number[] | null;
  /** startFrom 게이트가 활성(미제출) 상태인지 */
  isGateActive: boolean;
  ctaFormPage?: number;
  embedItems?: EmbedItemType[];
  isMobile: boolean;
  translateKoToEn: (item: string) => string;
  /** 데스크톱 패널 접기(헤더 우측 버튼) */
  onClose?: () => void;
}

const ThumbnailItem = ({
  pageData,
  isCurrent,
  isLocked,
  isBlind,
  isMobile,
  embeds,
  onSelect,
}: {
  pageData: V2PageType;
  isCurrent: boolean;
  isLocked: boolean;
  isBlind: boolean;
  isMobile: boolean;
  embeds?: EmbedType[];
  onSelect: () => void;
}) => {
  const itemRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (isCurrent) {
      itemRef.current?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    }
  }, [isCurrent]);

  const aspect = pageData.originalWidth / pageData.originalHeight || 1;

  return (
    <Styled.Item
      ref={itemRef}
      type="button"
      isMobile={isMobile}
      className={cn({ 'is-current': isCurrent })}
      aria-current={isCurrent ? 'page' : undefined}
      onClick={(e) => {
        e.stopPropagation();
        onSelect();
      }}
    >
      <Styled.ThumbBox
        isMobile={isMobile}
        style={
          isMobile
            ? { aspectRatio: `${aspect}` }
            : {
                aspectRatio: `${aspect}`,
                width: `min(100%, calc(120px * ${aspect}))`,
              }
        }
      >
        {pageData.imageSources?.thumb ? (
          <Styled.PanelThumbImage
            className={cn({ 'is-blind': isBlind || isLocked })}
            thumb={pageData.imageSources.thumb}
            alt={`pdf thumbnail ${pageData.page}page`}
            fill
          />
        ) : (
          <Styled.LegacyThumbImage
            className={cn({ 'is-blind': isBlind || isLocked })}
            src={pageData.src}
            alt={`pdf thumbnail ${pageData.page}page`}
            loading="lazy"
            decoding="async"
            draggable={false}
          />
        )}
        {/* 가림/잠금 페이지에서는 페이지 이미지와 동일하게 블러 처리해 노출한다 */}
        {embeds?.map((embed) => (
          <Styled.EmbedOverlay
            key={embed.id}
            className={cn({ 'is-blind': isBlind || isLocked })}
            style={{
              left: `${embed.rect_x * 100}%`,
              top: `${embed.rect_y * 100}%`,
              width: `${embed.rect_w * 100}%`,
              height: `${embed.rect_h * 100}%`,
            }}
          >
            {embed.thumbnailUrl ? (
              <img src={embed.thumbnailUrl} alt="" />
            ) : (
              <Icon icon="ph:play-fill" size={12} />
            )}
          </Styled.EmbedOverlay>
        ))}
        {isLocked && (
          <Styled.LockOverlay alignItems="center" justifyContent="center">
            <Icon icon="ph:lock-simple" size={20} color="white" />
          </Styled.LockOverlay>
        )}
      </Styled.ThumbBox>
      <Text
        variant="sm-medium"
        color={isCurrent ? 'skyblue_500' : 'zinc_400'}
        textAlign="center"
      >
        {pageData.page}
      </Text>
    </Styled.Item>
  );
};

/**
 * V2 페이지 목록 패널. 데스크톱은 좌측 패널, 모바일은 하단 가로 스트립.
 * 전체 페이지 목록 기반 + 네이티브 지연 로딩으로 V1의 증분 하이드레이션
 * 빈 슬롯 문제를 제거한다. 게이트 너머 페이지는 잠금 표시한다.
 */
const ThumbnailPanel = ({
  document,
  currentPage,
  goToPage,
  blindPages,
  isGateActive,
  ctaFormPage,
  embedItems,
  isMobile,
  translateKoToEn,
  onClose,
}: Props) => {
  const items = document.pages.map((pageData) => {
    const isLocked =
      isGateActive && ctaFormPage !== undefined && pageData.page > ctaFormPage;

    return (
      <ThumbnailItem
        key={pageData.page}
        pageData={pageData}
        isCurrent={pageData.page === currentPage}
        isLocked={isLocked}
        isBlind={Boolean(blindPages?.includes(pageData.page))}
        isMobile={isMobile}
        embeds={embedItems?.find((item) => item.page === pageData.page)?.embed}
        onSelect={() => goToPage(pageData.page)}
      />
    );
  });

  return (
    <Styled.Panel
      isMobile={isMobile}
      role="listbox"
      aria-label={translateKoToEn('thumbnails')}
    >
      {isMobile ? (
        items
      ) : (
        <>
          <Styled.PanelHeader>
            <Styled.PanelHeaderRow>
              <Styled.PanelTitle variant="sm-semibold" color="zinc_700">
                {document.name}
              </Styled.PanelTitle>
              {onClose && (
                <Styled.PanelCloseButton
                  type="button"
                  aria-label={translateKoToEn('desktop-pageList-close')}
                  title={translateKoToEn('desktop-pageList-close')}
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                >
                  <Icon
                    icon="ph:caret-double-left"
                    size={16}
                    color="zinc_400"
                  />
                </Styled.PanelCloseButton>
              )}
            </Styled.PanelHeaderRow>
            <Text variant="xs-medium" color="zinc_400">
              {translateKoToEn('total-page-count').replace(
                '{{count}}',
                String(document.totalPages),
              )}
            </Text>
          </Styled.PanelHeader>
          <Styled.PanelScroll>{items}</Styled.PanelScroll>
        </>
      )}
    </Styled.Panel>
  );
};

export default ThumbnailPanel;
